'use client';

import { ArrowUpRight } from 'lucide-react';
import Image from 'next/image';
import React, { useRef, useState } from 'react';
import { useAnalytics } from '@/services/frontendAnalyticsService';

interface HeroVideoSectionProps {
  videoUrl?: string;
  posterUrl?: string;
}

function FallbackHeroSection() {
  const analytics = useAnalytics();

  return (
    <section className="relative min-h-[85vh] lg:min-h-[90vh] bg-[#F8F9FA] text-neutral-900 flex flex-col pb-16 lg:pb-20 overflow-hidden">
      <div className="h-16 md:h-20 lg:h-24 shrink-0" />
      <div className="flex-1 flex items-center">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
            linear-gradient(to right, #000000 1px, transparent 1px),
            linear-gradient(to bottom, #000000 1px, transparent 1px)
          `,
            backgroundSize: '4rem 4rem',
          }}
        />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col-reverse lg:grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-center">
          <div className="max-w-2xl">
            <h1 className="text-fluid-3xl md:text-fluid-4xl lg:text-fluid-5xl font-bold tracking-tight leading-[1.05] mb-4 md:mb-6 text-neutral-900">
              Mengurai Kompleksitas <br />
              <span className="text-neutral-500">Hukum Indonesia</span>
            </h1>

            <p className="text-fluid-sm md:text-fluid-base text-neutral-600 leading-relaxed mb-6 md:mb-8 max-w-xl">
              Riset 270.000+ peraturan dan 4.500+ konsolidasi dalam hitungan detik. Ekstrak informasi dan review banyak dokumen hukum dengan mudah.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-3">
              <button
                onClick={() => {
                  analytics.landingPage.demoCTAClicked({ location: 'hero' });
                  window.open(
                    'https://wa.me/6282313436274?text=Halo%2C%20saya%20tertarik%20untuk%20demo%20Justisio%20untuk%20perusahaan%20kami.',
                    '_blank'
                  );
                }}
                className="group inline-flex items-center justify-center gap-2 px-4 py-2 bg-transparent text-neutral-900 border-2 border-neutral-900 text-sm font-semibold rounded-lg hover:bg-neutral-100 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 w-full sm:w-auto"
              >
                Jadwalkan Demo
                <ArrowUpRight
                  size={16}
                  className="text-neutral-500 group-hover:text-neutral-900 transition-colors"
                />
              </button>
            </div>
          </div>

          <div className="relative h-[250px] md:h-[400px] lg:h-[450px] w-full overflow-hidden mb-4 lg:mb-0">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-full md:w-[90%] bg-white rounded-2xl overflow-hidden z-10 shadow-lg">
              <Image
                src="/static/images/landing/hero/jakarta-cityscape-1200.webp"
                alt="Pemandangan kota Jakarta yang menampilkan gedung-gedung pencakar langit modern, merepresentasikan lingkungan bisnis dan hukum Indonesia"
                width={1200}
                height={900}
                sizes="(max-width: 768px) 100vw, 85vw"
                className="w-full h-[250px] md:h-[350px] object-cover grayscale-[0.2] md:scale-105"
                priority
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HeroVideoSection({ videoUrl, posterUrl }: HeroVideoSectionProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const analytics = useAnalytics();

  const defaultPoster = '/public-assets/assets/ec80e4d7-6379-4541-b317-b740f2c7a0e6.webp';

  if (videoUrl) {
    return (
      <section className="relative h-[95vh] lg:h-screen w-full bg-neutral-950 text-white flex items-end overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onLoadedData={() => setIsVideoLoaded(true)}
          className="absolute inset-0 w-full h-full object-cover"
          poster={posterUrl || defaultPoster}
        >
          <source src={videoUrl} type="video/mp4" />
          <source src={videoUrl} type="video/webm" />
        </video>

        {!isVideoLoaded && (
          <Image
            src={posterUrl || defaultPoster}
            alt="Hero background"
            fill
            className="object-cover"
            priority
          />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/40" />

        <div className="relative z-10 w-full px-6 md:px-12 pt-8 pb-16 md:py-12">
          <div className="max-w-5xl mx-auto flex flex-col items-center text-center">
            <h1 className="text-fluid-4xl md:text-fluid-5xl font-bold tracking-tight leading-[1.1] mb-4 md:mb-5 max-w-4xl text-white">
              Mengurai Kompleksitas Hukum Indonesia
            </h1>

            <p className="text-fluid-base md:text-fluid-lg text-white/80 leading-relaxed mb-6 md:mb-8 max-w-2xl mx-auto">
              Riset 270.000+ peraturan dan 4.500+ konsolidasi dalam hitungan detik. Ekstrak informasi dan review banyak dokumen hukum dengan mudah.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full sm:w-auto">
              <button
                onClick={() => {
                  analytics.landingPage.demoCTAClicked({ location: 'hero' });
                  window.open(
                    'https://wa.me/6282313436274?text=Halo%2C%20saya%20tertarik%20untuk%20demo%20Justisio%20untuk%20perusahaan%20kami.',
                    '_blank'
                  );
                }}
                className="group inline-flex items-center justify-center gap-2 px-6 py-3 bg-transparent text-white border-2 border-white text-base font-semibold rounded-full hover:bg-white/10 transition-all duration-200 w-full sm:w-auto"
              >
                Jadwalkan Demo
                <ArrowUpRight
                  size={18}
                  className="text-white group-hover:text-white transition-colors"
                />
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return <FallbackHeroSection />;
}
